import { useEffect, useState } from 'react';

/**
 * Web Share API (Level 2 — file sharing) helpers.
 *
 * The PDF download in the CRM Belegtool serves a presigned iDrive-S3 URL via
 * `window.open(...)`. To additionally *share* the PDF through the OS share sheet
 * (`navigator.share({ files })`) we must first load the URL as a Blob — which
 * requires the bucket to allow cross-origin GET. The iDrive bucket is CORS-enabled
 * for the client origins, so the `fetch` below succeeds in the browser.
 */

const PDF_MIME = 'application/pdf';

/**
 * True when the current browser can share PDF *files* via the Web Share API.
 * SSR-safe: returns false when `navigator` is unavailable. File sharing is mostly
 * available on mobile (iOS Safari, Android Chrome); desktop browsers usually return
 * false here, in which case callers should keep the plain download.
 */
export function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare) {
    return false;
  }
  try {
    const probe = new File(['%PDF-'], 'probe.pdf', { type: PDF_MIME });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/**
 * React hook variant of {@link canShareFiles} that is hydration-safe: it returns
 * `false` during SSR and the first client render, then resolves the real value
 * after mount to avoid a server/client markup mismatch.
 */
export function useCanShareFiles(): boolean {
  const [supported, setSupported] = useState(false);
  useEffect(() => {
    setSupported(canShareFiles());
  }, []);
  return supported;
}

/**
 * Turn a document number into a safe PDF filename, e.g. "RG/2024/123" -> "RG-2024-123.pdf".
 */
export function sanitizePdfFilename(documentNumber: string): string {
  const base = (documentNumber || 'beleg')
    .replace(/[/\\:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')
    .replace(/^-+|-+$/g, '')
    .trim();
  return `${base || 'beleg'}.pdf`;
}

export type SharePdfResult = 'shared' | 'cancelled' | 'fallback';

/**
 * Fetch the PDF at `url` and share it as a file via the Web Share API.
 *
 * Returns:
 * - `'shared'`    — the share sheet completed,
 * - `'cancelled'` — the user dismissed the share sheet (no fallback wanted),
 * - `'fallback'`  — sharing is unsupported or failed; the caller should fall back
 *                   to the normal download (e.g. `window.open(url)`).
 *
 * Note: on some iOS Safari versions the transient user activation can be lost
 * across the `await fetch(...)`, causing `navigator.share` to reject with a
 * non-abort error. That path returns `'fallback'`, so the download still works.
 */
export async function sharePdfFromUrl(
  url: string,
  filename: string
): Promise<SharePdfResult> {
  if (!canShareFiles()) {
    return 'fallback';
  }

  let file: File;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return 'fallback';
    }
    const blob = await response.blob();
    file = new File([blob], filename, { type: blob.type || PDF_MIME });
  } catch {
    return 'fallback';
  }

  if (!navigator.canShare?.({ files: [file] })) {
    return 'fallback';
  }

  try {
    await navigator.share({ files: [file], title: filename });
    return 'shared';
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return 'cancelled';
    }
    return 'fallback';
  }
}
